import { Card } from "@/components/ui/card";
import { LucideIcon, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface FinancialMetricCardProps {
  title: string;
  amount: number;
  currency?: string;
  icon: LucideIcon;
  trend?: "up" | "down";
  trendValue?: string;
  color?: "primary" | "success" | "warning" | "info" | "destructive";
}

const colorClasses: Record<string, string> = {
  primary: "text-primary bg-primary/10",
  success: "text-success bg-success/10",
  warning: "text-warning bg-warning/10",
  info: "text-info bg-info/10",
  destructive: "text-destructive bg-destructive/10",
};

const borderClasses: Record<string, string> = {
  primary: "border-l-primary",
  success: "border-l-success",
  warning: "border-l-warning",
  info: "border-l-info",
  destructive: "border-l-destructive",
};

export const FinancialMetricCard = ({
  title,
  amount,
  currency = "₹",
  icon: Icon,
  trend,
  trendValue,
  color = "primary",
}: FinancialMetricCardProps) => {
  const swatch = colorClasses[color] ?? colorClasses.primary;
  const border = borderClasses[color] ?? borderClasses.primary;

  return (
    <Card className={cn("p-4 shadow-sm border-l-4", border)}>
      <div className="flex items-center justify-between mb-2.5">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${swatch}`}>
          <Icon className="w-4.5 h-4.5" size={18} />
        </div>
        {trend && trendValue && (
          <span className={`flex items-center gap-1 text-[11px] font-semibold ${trend === "up" ? "text-success" : "text-destructive"}`}>
            {trend === "up" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trendValue}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-foreground tabular-nums leading-tight">
        {currency}
        {amount.toLocaleString("en-IN")}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{title}</p>
    </Card>
  );
};
